import * as React from "react"
import { cn } from "@/lib/utils"

export interface DirectoryItem {
  title: string
  description: string
  href: string
  icon?: React.ReactNode
  status?: "ready" | "coming" | "development" | "beta"
  category?: string
  onClick?: () => void
}

interface DirectoryListProps extends React.HTMLAttributes<HTMLDivElement> {
  items: DirectoryItem[]
  columns?: number
  showStatus?: boolean
}

export function DirectoryList({
  items,
  columns = 2,
  showStatus = false,
  className,
  ...props
}: DirectoryListProps) {
  return (
    <div
      className={cn(
        "grid gap-4",
        columns === 1 && "grid-cols-1",
        columns === 2 && "grid-cols-1 sm:grid-cols-2",
        columns === 3 && "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
        columns === 4 && "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
        className
      )}
      {...props}
    >
      {items.map((item, index) => (
        <div
          key={index}
          className={cn(
            "group relative rounded-lg border bg-card p-4 hover:bg-accent/50 transition-colors",
            item.onClick && "cursor-pointer"
          )}
          onClick={item.onClick}
        >
          <div className="flex items-start space-x-3">
            {item.icon && (
              <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-muted">
                {item.icon}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-sm text-foreground group-hover:text-primary transition-colors">
                  {item.title}
                </h3>
                {showStatus && item.status && (
                  <span className={cn(
                    "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                    item.status === "ready" && "bg-green-100 text-green-800",
                    item.status === "beta" && "bg-blue-100 text-blue-800",
                    item.status === "development" && "bg-orange-100 text-orange-800",
                    item.status === "coming" && "bg-gray-100 text-gray-800"
                  )}>
                    {item.status}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {item.description}
              </p>
              {item.category && (
                <p className="text-xs text-muted-foreground mt-2 uppercase tracking-wide">
                  {item.category}
                </p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
